import { Accordion, AccordionDetails, AccordionSummary, Box, Grid } from "@mui/material"
import { InnerWrapper, MainContainer, TitleAndActionBox } from "../../../../common"
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { handleApi } from "../../../../../apis";
import { EXPORTTRANSACTION, GETSTATEMENTSTATUS } from "../../../../../apis/configuration";
import { ParagraphText, UBButton } from "../../../../../Libraries";
import { DownloadIconBlack, DropDown } from "../../../../../assets/icons/icon";
import RangePicker from "../../../../common/RangePicker";
import BusinessLoader from "../../../../common/BusinessLoader";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";
import { DATE_FILTERS_BANK_STATEMENT, sentenceCase } from "../../../../../constants";
import styles from "./../AllTransaction.module.css";

const YEARS_COUNT = 3;
const STATEMENT_POLL_DELAY = 3000;
const STATEMENT_POLL_MAX_ATTEMPTS = 40;

// Last 3 years; current year lists Jan → current month
const buildStatementYears = () => {
    const today = moment();
    const currentYear = today.year();
    return Array.from({ length: YEARS_COUNT }, (_, i) => {
        const year = currentYear - i;
        const lastMonth = year === currentYear ? today.month() : 11;
        const months = [];
        for (let m = lastMonth; m >= 0; m--) {
            months.push(moment({ year, month: m, day: 1 }));
        }
        return { year, months };
    }).filter(({ months }) => months.length > 0);
}

const ViewBankStatement = () => {
    const [I18n] = useContext(LanguageContext);
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const statementYears = useMemo(buildStatementYears, []);
    const [expandedYear, setExpandedYear] = useState(statementYears[0]?.year);
    const [isRangeOpen, setIsRangeOpen] = useState(false);
    const [loadingKey, setLoadingKey] = useState("");
    const pollRef = useRef(null);
    const idempotencyKey = useRef(uuidv4());
    const lastSubmittedRef = useRef("");
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearTimeout(pollRef.current);
        };
    }, []);

    const stopStatementPolling = () => {
        clearTimeout(pollRef.current);
        pollRef.current = null;
    };

    const handleStatementFailed = (message) => {
        stopStatementPolling();
        setLoadingKey("");
        showSnackbar({ message: message || I18n.something_went_wrong, severity: "error" });
    };

    // Download the generated PDF and open it in a new tab
    const downloadAndOpenStatement = async (fileUrl) => {
        if (!fileUrl) {
            showSnackbar({ message: I18n.download_url_not_available, severity: "error" });
            return;
        }
        try {
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
            const fileName = decodeURIComponent(fileUrl.split("?")[0].split("/").pop() || "statement.pdf");

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.open(blobUrl, "_blank");
            // Keep the blob alive long enough for the new tab to load it
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);

            showSnackbar({ message: I18n.pdf_downloaded_successfully, severity: "success" });
        } catch (error) {
            console.error("Download error:", error);
            showSnackbar({ message: I18n.failed_to_download_file, severity: "error" });
        }
    };

    // Recursively poll report status; next call fires only after the previous response
    const pollStatementStatus = async (reportId, attempt = 1) => {
        if (!isMountedRef.current) return;
        try {
            const resp = await handleApi(GETSTATEMENTSTATUS, { report_id: reportId }, null, navigate);
            if (!isMountedRef.current) return;
            const status = resp?.status ?? resp?.data?.status;
            if (status === "GENERATED") {
                stopStatementPolling();
                const fileUrl = resp?.file_url ?? resp?.data?.file_url;
                triggerLogEvent(analytics, "emi_wlt_bank_statement_generated", { report_id: reportId, file_url: fileUrl });
                await downloadAndOpenStatement(fileUrl);
                if (isMountedRef.current) setLoadingKey("");
                return;
            }
            if (status === "FAILED" || attempt >= STATEMENT_POLL_MAX_ATTEMPTS) {
                handleStatementFailed();
                return;
            }
            pollRef.current = setTimeout(
                () => pollStatementStatus(reportId, attempt + 1),
                STATEMENT_POLL_DELAY,
            );
        } catch (error) {
            if (isMountedRef.current) handleStatementFailed(error?.response?.data?.message);
        }
    };

    const requestStatement = async (startDate, endDate, key) => {
        if (!startDate || !endDate || loadingKey) return;
        setLoadingKey(key);
        const payload = {
            start_date: moment(startDate).format("YYYY-MM-DD"),
            end_date: moment(endDate).format("YYYY-MM-DD"),
            filter: {},
            delete: key !== "custom",
        };
        // Only mint a new idempotency key if the request changed since the last attempt,
        // so retries of the same request stay idempotent.
        const currentSubmission = JSON.stringify(payload);
        if (currentSubmission !== lastSubmittedRef.current) {
            idempotencyKey.current = uuidv4();
        }
        lastSubmittedRef.current = currentSubmission;
        payload.idempotency_key = idempotencyKey.current;
        triggerLogEvent(analytics, "emi_wlt_bank_statement_request", payload);
        try {
            const resp = await handleApi(EXPORTTRANSACTION, payload, null, navigate);
            // Custom range: no polling, go straight to the statements list
            if (key === "custom") {
                setLoadingKey("");
                navigate("/Ucash/CustomStatement");
                return;
            }
            const reportId = resp?.report_id ?? resp?.data?.report_id;
            if (!reportId) {
                handleStatementFailed();
                return;
            }
            showSnackbar({ message: I18n.request_placed, severity: "success", toastId: "statement_request_placed" });
            showSnackbar({ message: I18n.downloading_statement, severity: "info", toastId: "statement_downloading" });
            pollStatementStatus(reportId);
        } catch (error) {
            handleStatementFailed(error?.response?.data?.message);
        }
    }

    const handleMonthDownload = (month) => {
        const monthEnd = month.clone().endOf("month");
        requestStatement(
            month.clone().startOf("month"),
            moment.min(monthEnd, moment()),
            month.format("YYYY-MM"),
        );
    }

    return (
        <MainContainer fluid className="quickSalePage">
            <BusinessLoader loadBusiness={!!loadingKey} className={styles.statementOverlayLoader} />
            <Grid container>
                <Grid item md={12}>
                    <TitleAndActionBox
                        data={{
                            title: I18n.bank_statement,
                            isBack: true,
                            onBack: () => navigate(-1),
                            id: "bank_statement",
                        }}
                    >
                        <Box className={styles.bankStatementActions}>
                            <Box className={styles.customRangeWrap}>
                                <UBButton
                                    id="bank_statement_custom_range_btn"
                                    title={I18n.custom_date_range}
                                    className={styles.bankStatementBtn}
                                    disabled={!!loadingKey}
                                    onClick={() => setIsRangeOpen(!isRangeOpen)}
                                />
                                {isRangeOpen && (
                                    <RangePicker
                                        onFilterDate={(startDate, endDate) => {
                                            setIsRangeOpen(false);
                                            requestStatement(startDate, endDate, "custom");
                                        }}
                                        isOpen={isRangeOpen}
                                        setIsOpen={setIsRangeOpen}
                                        showDirectPicker={true}
                                        rangeValue={[]}
                                        className={styles.bankStatementRangePicker}
                                        noClearBtn={true}
                                        maxDate={dayjs()}
                                        startDateLabel={sentenceCase(I18n.start_date)}
                                        endDateLabel={sentenceCase(I18n.end_date)}
                                        applyBtnText={I18n.apply}
                                        dateFilters={DATE_FILTERS_BANK_STATEMENT}
                                    />
                                )}
                            </Box>
                            <UBButton
                                id="bank_statement_custom_statements_btn"
                                title={I18n.custom_statements}
                                className={styles.bankStatementBtn}
                                onClick={() => {
                                    triggerLogEvent(analytics, "emi_wlt_custom_statements_view");
                                    navigate("/Ucash/CustomStatement");
                                }}
                            />
                        </Box>
                    </TitleAndActionBox>
                    <InnerWrapper className={`innerWrapper newInnerWrapper ${styles.bankStatementWrapper}`}>
                        <Box className={`${styles.statementYearList} generic-scrollbar`}>
                            {statementYears.map(({ year, months }) => (
                                <Accordion
                                    key={year}
                                    expanded={expandedYear === year}
                                    onChange={() => setExpandedYear(expandedYear === year ? null : year)}
                                    className={styles.statementYearAccordion}
                                    disableGutters
                                    sx={{ boxShadow: "none", "&:before": { display: "none" } }}
                                >
                                    <AccordionSummary
                                        id={`bank_statement_year_${year}`}
                                        expandIcon={<DropDown width={16} height={16} />}
                                    >
                                        <ParagraphText className="fontSize16 fontWeight500 newLightColor">
                                            {year}
                                        </ParagraphText>
                                    </AccordionSummary>
                                    <AccordionDetails className={styles.statementMonthList}>
                                        {months.map((month) => {
                                            const key = month.format("YYYY-MM");
                                            return (
                                                <Box key={key} className={styles.statementMonthRow}>
                                                    <ParagraphText className="fontSize14 fontWeight400 newFontColor">
                                                        {month.format("MMMM")}
                                                    </ParagraphText>
                                                    <DownloadIconBlack
                                                        id={`bank_statement_download_${key}`}
                                                        width={16}
                                                        height={16}
                                                        className={`cursorPointer ${styles.statementMonthDownload}`}
                                                        onClick={() => handleMonthDownload(month)}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    </InnerWrapper>
                </Grid>
            </Grid>
        </MainContainer>
    )
}

export default ViewBankStatement;
